import { Alert, AlertProps, Card, CardContent, Snackbar } from "@mui/material";
import { DataGrid, GridGetRowsError, GridLogicOperator, GridRowsProp, GridRowModesModel, GridUpdateRowError, GridEventListener, GridRowEditStopReasons, GridRowModes, GridRowId, GridRowModel, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import React from "react";
import CustomNoRowsOverlay from "./CustomNoRowsOverlay";
import CustomToolbar from "./CustomToolbar";
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import axios from "axios";

interface EditToolbarProps {
    rows: GridRowsProp;
    rowModesModel: GridRowModesModel;
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (
        newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
    ) => void;
}

export default function CustomDataGrid(props: any) {
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const [rows, setRows] = React.useState<GridRowsProp>([]);
    const [quickFilterValues, setQuickFilterValues] = React.useState<string[]>([]);
    const { message, gridDataSource, apiRef, lowTTLCache, columnVisibilityModel, navigate, defineMaxHeight, pageSize } = props;
    // const [columns, setColumns] = React.useState<GridColDef[]>(
    //     [...props.columns, 
    //         props.actions && props.actions.length > 0 && { 
    //             field: 'actions', type: 'actions', headerName: 'Actions', width: 100, cellClassName: 'actions', 
    //             getActions: ({ id, row }) => {
    //                 const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
    //                 if (isInEditMode) {
    //                     return [
    //                         <GridActionsCellItem icon={<SaveIcon />} label="Save" onClick={handleSaveClick(id)} />,
    //                         <GridActionsCellItem icon={<CancelIcon />} label="Cancel" onClick={handleCancelClick(id)} />,
    //                     ];
    //                 }else {
    //                     return [
    //                         <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={handleEditClick(id)} />,
    //                     ];
    //                 }    
    //                 return undefined;
    //             }
    //         }
    //     ]
    // )
    const { title, url, exportUrl } = props;
    const { sortModel } = props;
    const { tab } = props;
    const { actions } = props;
    const { fullFeatured } = props;
    
    const handleRowEditStop: GridEventListener<'rowEditStop'> = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
          event.defaultMuiPrevented = true;
        }
    };

    const handleEditClick = (id: GridRowId) => () => {
        setTimeout(() => {
            setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
        }, 100);
    };
    
    const handleSaveClick = (id: GridRowId) => () => {
        setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
    };

    const handleCancelClick = (id: GridRowId) => () => {
        setTimeout(() => {
            setRowModesModel({
                ...rowModesModel,
                [id]: { mode: GridRowModes.View, ignoreModifications: true },
            });
        }, 100);
    
        const editedRow = rows.find((row) => row.id === id);
        if (editedRow!.isNew) {
          setRows(rows.filter((row) => row.id !== id));
        }
    };

    const processRowUpdate = async (newRow: GridRowModel) => {
        const updatedRow = { ...newRow, isNew: false };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        const csrfToken = await axios.get('/csrf').then((response) => response.data.csrfToken);
        fetch(`${url}/${newRow.id}/?entity_id=${localStorage.getItem('entity_id')}`, {
            method: 'PUT',
            headers: {
                'X-CSRFToken': csrfToken,
            },
            body: JSON.stringify(updatedRow),
        }).then((response: any) => {
            if (response.ok) {
                setSnackbar({ children: 'Object updated successfully', severity: 'success' });
            } else {
                setSnackbar({ children: 'Failed to update object', severity: 'error' });
            }
        }).catch((error) => {
            setSnackbar({ children: error.message, severity: 'error' });
        });
        return updatedRow;
    };

    const columns = [
        ...props.columns,
        ...(actions && actions.length > 0 ? [{
            field: 'actions', 
            type: 'actions', 
            headerName: 'Actions', 
            width: 100, 
            cellClassName: 'actions',
            renderHeader: (params: { colDef: { headerName: string; }; }) => {
                return <span style={{ fontWeight: 'bold' }}>{params.colDef.headerName as string}</span>;
            },
            getActions: ({ id }: { id: GridRowId }) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
                if (isInEditMode) {
                    return [
                        <GridActionsCellItem icon={<SaveIcon />} label="Save" onClick={handleSaveClick(id)} />,
                        <GridActionsCellItem icon={<CancelIcon />} label="Cancel" onClick={handleCancelClick(id)} />,
                    ];
                }else {
                    return [
                        <GridActionsCellItem icon={<EditIcon />} label="Edit" onClick={handleEditClick(id)} />,
                    ];
                }
            }
        }] : []),
    ]

    const [snackbar, setSnackbar] = React.useState<Pick<
        AlertProps,
        'children' | 'severity'
    > | null>(null);

    // const handleProcessRowUpdate = React.useCallback(
    //     async (newRow: GridRowModel) => {
    //       // Make the HTTP request to save in the backend
    //       const response = await mutateRow(newRow);
    //       setSnackbar({ children: 'User successfully saved', severity: 'success' });
    //       return response;
    //     },
    //     [mutateRow],
    // );

    const handleCloseSnackbar = () => setSnackbar(null);

    const handleProcessRowUpdateError = React.useCallback((error: Error) => {
        setSnackbar({ children: error.message, severity: 'error' });
    }, []);
    
    return (
        <React.Fragment>
            <DataGrid
                ignoreDiacritics={true}
                apiRef={apiRef}
                rows={gridDataSource}
                columns={columns}
                dataSourceCache={lowTTLCache}
                onDataSourceError={(error) => {
                    if (error instanceof GridGetRowsError) {
                        // `error.params` is of type `GridGetRowsParams`
                        // fetch related logic, e.g set an overlay state
                    }
                    if (error instanceof GridUpdateRowError) {
                        // `error.params` is of type `GridUpdateRowParams`
                        // update related logic, e.g set a snackbar state
                    }
                }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                        quickFilterProps: { debounceMs: 500 }
                    },
                    ...(actions && actions.length > 0 ? {
                        toolbar: {
                            setRows,
                            setRowModesModel,
                        }
                    } : {}),
                }}
                pagination
                initialState={{ 
                    pagination: { paginationModel: { pageSize: pageSize ? pageSize : pageSize === undefined ? 10 : pageSize, page: 0 }, rowCount: 0 }, 
                    filter: { 
                        filterModel: { 
                            items: [], 
                            quickFilterLogicOperator: GridLogicOperator.Or,
                        } 
                    },
                    sorting: {
                        sortModel: sortModel.length > 0 ? sortModel : [],
                    },
                }}
                pageSizeOptions={[5, 10, 20, 50, 100]}
                dataSource={gridDataSource}
                slots={{ 
                    noRowsOverlay: () => <CustomNoRowsOverlay message={message} />, 
                    toolbar: (props: any) => <CustomToolbar 
                        {...props} 
                        title={title} 
                        url={url} 
                        fullFeatured={fullFeatured}
                        exportUrl={exportUrl + `${exportUrl.includes('?') ? '&' : '?'}entity_id=${localStorage.getItem('entity_id')}`} 
                        apiRef={apiRef} 
                    /> 
                }}
                editMode="row"
                isRowSelectable={() => false}
                rowModesModel={rowModesModel}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                onRowModesModelChange={(newRowModesModel: GridRowModesModel) => setRowModesModel(newRowModesModel)}
                onRowEditStop={handleRowEditStop}
                onRowClick={(params) => {
                    if (navigate || tab === 'C') {
                        window.location.href = tab === 'C' ? `/customers/${params.row.item_id}?entity_id=${localStorage.getItem('entity_id')}` : `/customers/${params.row.cust_id}?entity_id=${localStorage.getItem('entity_id')}`;
                    }
                }}
                columnVisibilityModel={columnVisibilityModel}
                showToolbar
                autosizeOnMount={false}
                getRowHeight={() => fullFeatured ? 30 : 'auto'}
                sx={{
                    width: '100%', 
                    // minHeight: 400, 
                    height: 'auto',
                    fontFamily: 'inherit',
                    maxHeight: defineMaxHeight ? defineMaxHeight : 'auto', 
                    borderRadius: '6px', 
                    overflowY: 'auto !important',
                    overflowX: 'auto !important',
                    border: 'none',
                    // boxShadow: '0px 0px 10px 0px rgba(0, 0, 0, 0.1)',

                    // borderWidth: 0,
                    '& .MuiDataGrid-row': {
                        minHeight: '30px !important',
                        // display: 'flex',
                        // alignItems: 'center',
                    },
                    '& .MuiDataGrid-cell': {
                        whiteSpace: 'normal !important',
                        lineHeight: 'inherit !important',
                        display: 'flex',
                        alignItems: 'center',
                    },
                    '& .MuiDataGrid-cell:hover': {
                        color: 'primary.main',
                    },
                    '& .MuiDataGrid-row:hover': {
                        cursor: 'pointer',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        whiteSpace: 'normal',
                        lineHeight: 'normal',
                    },
                    '& .MuiDataGrid-columnHeader': {
                        // Forced to use important since overriding inline styles
                        height: 'unset !important',
                    },
                    fontSize: '0.75rem',
                }}            
            />
            {!!snackbar && (
                <Snackbar open={!!snackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                    <Alert {...snackbar} onClose={handleCloseSnackbar} />
                </Snackbar>
            )}
        </React.Fragment>
    )
 }